import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { useWindowWidth } from '../hooks/useWindowWidth';

/* ─── Design tokens ──────────────────────────────────────────── */
const INK   = '#0A1F4A';
const INK2  = '#3D4F78';
const INK3  = '#7883A2';
const BG    = '#ECEEF3';
const SURF  = '#FFFFFF';
const RULE  = '#D6DAE4';
const ACC   = '#FF7A3D';
const HOT   = '#FFD500';

const container = { maxWidth: 1200, margin: '0 auto', padding: '0 24px' };

const logos = [
  'Norddeutsche', 'Vessel & Co.', 'Helios Partners', 'Stichting Blauw', 'Nordkap',
  'AgroTech NL', 'EuroServ S.A.',
];

/* ─── Shared: section kicker ─────────────────────────────────── */
function Kicker({ children }) {
  return (
    <span style={{
      display: 'inline-block',
      fontFamily: '"JetBrains Mono", monospace',
      fontSize: 11, fontWeight: 600,
      letterSpacing: '0.14em', textTransform: 'uppercase',
      padding: '4px 10px',
      background: INK, color: ACC,
      borderRadius: 4,
      marginBottom: 16,
    }}>
      {children}
    </span>
  );
}

/* ─── Section: Hero ──────────────────────────────────────────── */
function Hero({ isMobile }) {
  return (
    <section style={{ padding: isMobile ? '60px 0 72px' : '80px 0 100px', background: BG, position: 'relative', overflow: 'hidden' }}>
      <div style={{ ...container, display: isMobile ? 'block' : 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: 64, alignItems: 'center' }}>

        {/* Left */}
        <div style={isMobile ? { marginBottom: 48 } : {}}>
          {/* Eyebrow */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: SURF, border: `1px solid ${INK}`,
            padding: '6px 12px', borderRadius: 999,
            fontSize: 12, fontWeight: 500, color: INK,
            marginBottom: 20,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: ACC, boxShadow: `0 0 0 3px rgba(255,122,61,0.25)` }} />
            <span>Yesterday's EU tenders, already scored</span>
          </div>

          {/* Headline */}
          <h1 style={{
            fontSize: isMobile ? 40 : 68,
            lineHeight: 0.98,
            letterSpacing: '-0.035em',
            fontWeight: 700,
            marginBottom: 22,
            color: INK,
          }}>
            Yesterday's EU tenders,{' '}
            <span style={{
              background: ACC,
              padding: '0 6px',
              display: 'inline-block',
              transform: 'rotate(-1deg) translateY(-2px)',
              boxShadow: `4px 4px 0 ${INK}`,
              color: '#fff',
            }}>
              already
            </span>
            {' '}scored<br />for you.
          </h1>

          <p style={{ fontSize: 19, color: INK2, maxWidth: 520, marginBottom: 32, lineHeight: 1.5 }}>
            Jyto processes every new EU tender daily, scores each one against your company profile, and delivers all the tenders that match your business, every morning.
          </p>

          {/* CTAs */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <Link to="/signup" style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '12px 20px', borderRadius: 10,
              fontSize: 15, fontWeight: 600,
              background: ACC, color: '#fff',
              boxShadow: `0 4px 0 0 ${INK}`,
              textDecoration: 'none',
            }}>
              Start free trial →
            </Link>
            <Link to="/how-it-works" style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '12px 18px', borderRadius: 10,
              fontSize: 15, fontWeight: 500,
              color: INK, background: 'transparent',
              border: `1px solid ${INK}`,
              textDecoration: 'none',
            }}>
              See how it works
            </Link>
          </div>

          {/* Trust row */}
          <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: INK2 }}>
            <div style={{ display: 'flex' }}>
              {['#FF7A3D', '#FFD500', '#0A1F4A', '#3D4F78'].map((c, i) => (
                <div key={i} style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: c, border: `2px solid ${BG}`,
                  marginLeft: i === 0 ? 0 : -8,
                }} />
              ))}
            </div>
            <span><strong>1,240+</strong> bid teams across 11 sectors</span>
          </div>
        </div>

        {/* Right — preview card */}
        <div style={{
          background: SURF,
          border: `1px solid ${INK}`,
          borderRadius: 16,
          boxShadow: `12px 12px 0 ${INK}`,
          overflow: 'hidden',
          transform: isMobile ? 'none' : 'rotate(1deg)',
        }}>
          {/* Window bar */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 14px', borderBottom: `1px solid ${RULE}`, background: BG,
          }}>
            <div style={{ display: 'flex', gap: 5 }}>
              {[ACC, HOT, ACC].map((c, i) => (
                <div key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: i === 0 ? ACC : i === 1 ? HOT : RULE }} />
              ))}
            </div>
            <div style={{ fontSize: 11, color: INK3, fontFamily: '"JetBrains Mono", monospace' }}>~/matches · live</div>
            <div />
          </div>
          {/* Match rows */}
          <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              {
                score: 94, verdict: 'great', title: 'Cloud migration · Port of Rotterdam',
                country: 'Netherlands', deadline: '14 Mar 2026', value: '€2.4M',
                reasoning: 'Strong match — your cloud infrastructure portfolio aligns with the Port\'s hybrid migration scope.',
                sectors: ['Cloud & IT'],
              },
              {
                score: 91, verdict: 'great', title: 'Renewable energy framework, Land Berlin',
                country: 'Germany', deadline: '18 Mar 2026', value: '€12M',
                reasoning: 'Framework aligns with your 4yr infrastructure and energy sector experience.',
                sectors: ['Energy', 'Infra'],
              },
              {
                score: 88, verdict: 'good', title: 'Cybersecurity audit · e-Health EE',
                country: 'Estonia', deadline: '9 Mar 2026', value: '€430k',
                reasoning: 'Sector fit: public health IT security aligns with your recent certifications.',
                sectors: ['Cybersecurity'],
              },
            ].map((r) => {
              const vc = r.verdict === 'great'
                ? { bg: '#dcfce7', color: '#15803d', label: 'Great fit' }
                : { bg: '#fef9c3', color: '#854d0e', label: 'Good fit' };
              return (
                <div key={r.title} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, color: '#0f172a', lineHeight: 1.4, flex: 1 }}>{r.title}</div>
                    <div style={{ fontWeight: 700, fontSize: 11, padding: '3px 8px', borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0, background: vc.bg, color: vc.color }}>{vc.label} · {r.score}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#64748b', marginBottom: 8, flexWrap: 'wrap' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20"/></svg>
                      {r.country}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                      {r.deadline}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                      {r.value}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: '#374151', background: '#f0f7ff', padding: '7px 10px', borderRadius: 6, borderLeft: '3px solid #2563eb', lineHeight: 1.5, marginBottom: 8 }}>
                    {r.reasoning}
                  </div>
                  <div style={{ display: 'flex', gap: 5, alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Sectors</span>
                    {r.sectors.map((s) => (
                      <span key={s} style={{ background: '#f1f5f9', color: '#475569', fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 20 }}>{s}</span>
                    ))}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <span style={{ background: '#fff', border: '1px solid #e2e8f0', color: '#374151', fontSize: 11, padding: '5px 10px', borderRadius: 7, fontWeight: 500 }}>View details →</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Section: Logo strip ────────────────────────────────────── */
function LogoStrip() {
  return (
    <section style={{
      padding: '24px 0',
      borderTop: `1px solid ${RULE}`,
      borderBottom: `1px solid ${RULE}`,
      background: SURF,
    }}>
      <div style={{ ...container, display: 'flex', alignItems: 'center', gap: 48, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: INK3, fontFamily: '"JetBrains Mono", monospace', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Trusted by bid desks at
        </span>
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          {logos.map((name) => (
            <span key={name} style={{
              fontSize: 16, fontWeight: 700, letterSpacing: '-0.02em',
              color: INK, opacity: 0.45,
            }}>
              {name}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Section: Features ──────────────────────────────────────── */
function Features({ isMobile }) {
  const features = [
    {
      num: '01',
      title: 'Total coverage, zero noise',
      desc: 'Every EU+UK tender portal, crawled every 12 minutes. Duplicates collapsed. Low-relevance buried by default.',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
        </svg>
      ),
    },
    {
      num: '02',
      title: 'AI scoring that explains itself',
      desc: 'Every tender gets a 0–100 score against your profile — and a one-line "why". No black box, no magic.',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
        </svg>
      ),
    },
    {
      num: '03',
      title: 'Delivered where you work',
      desc: 'Morning digest, Slack, HubSpot, or raw API. We meet your workflow — not the other way around.',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 3v18"/>
        </svg>
      ),
    },
  ];

  return (
    <section style={{ padding: '100px 0', background: BG }}>
      <div style={container}>
        <div style={{ maxWidth: 720, margin: '0 auto 56px', textAlign: 'center' }}>
          <Kicker>Built for bid desks</Kicker>
          <h2 style={{ fontSize: isMobile ? 32 : 52, lineHeight: 1.02, letterSpacing: '-0.03em', fontWeight: 700, marginBottom: 16, color: INK }}>
            Less searching. More winning.
          </h2>
          <p style={{ fontSize: 18, color: INK2 }}>
            Three things we do better than a spreadsheet of RSS feeds.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: 20,
        }}>
          {features.map((f, i) => (
            <div key={f.num} style={{
              background: SURF,
              border: `1px solid ${INK}`,
              borderRadius: 18,
              padding: '28px',
              display: 'flex', flexDirection: 'column', gap: 14,
              position: 'relative',
              transition: 'transform 220ms, box-shadow 220ms',
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = `0 8px 0 ${INK}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <span style={{
                position: 'absolute', top: 18, right: 22,
                fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: INK3,
              }}>{f.num}</span>
              <div style={{
                width: 44, height: 44,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: 10,
                background: i === 0 ? INK : i === 1 ? ACC : HOT,
                color: i === 0 ? ACC : i === 1 ? '#fff' : INK,
              }}>
                {f.icon}
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em', color: INK }}>{f.title}</h3>
              <p style={{ margin: 0, color: INK2, fontSize: 15 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Section: How it works ──────────────────────────────────── */
function HowItWorks({ isMobile }) {
  const steps = [
    { num: 'STEP 01', title: 'Describe your business', desc: 'Five questions about what you sell and where. Not a 40-field CPV code form.' },
    { num: 'STEP 02', title: 'We read every posting', desc: '14k+ EU tenders ingested daily across TED, national journals, and regional portals.' },
    { num: 'STEP 03', title: 'You get the shortlist', desc: 'Only matches that score 70+. In your inbox each morning, or forwarded to Slack or your CRM.' },
  ];

  return (
    <section style={{ padding: '100px 0', background: INK, position: 'relative', overflow: 'hidden' }}>
      {/* Glow accent */}
      <div style={{
        position: 'absolute', left: -80, bottom: -80,
        width: 320, height: 320, borderRadius: '50%',
        background: ACC, opacity: 0.15,
        filter: 'blur(40px)',
        pointerEvents: 'none',
      }} />

      <div style={{ ...container, position: 'relative', zIndex: 1 }}>
        <div style={{ maxWidth: 720, margin: '0 auto 56px', textAlign: 'center' }}>
          <Kicker>How it works</Kicker>
          <h2 style={{ fontSize: isMobile ? 32 : 52, lineHeight: 1.02, letterSpacing: '-0.03em', fontWeight: 700, marginBottom: 0, color: BG }}>
            From posting to shortlist in under 20 minutes.
          </h2>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: 24,
        }}>
          {steps.map((step) => (
            <div key={step.num} style={{
              padding: 28,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 16,
            }}>
              <div style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 12, color: ACC, letterSpacing: '0.1em',
                marginBottom: 14,
              }}>{step.num}</div>
              <h3 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em', color: BG, marginBottom: 10 }}>{step.title}</h3>
              <p style={{ color: 'rgba(236,238,243,0.7)', fontSize: 15, margin: 0 }}>{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Section: Guide teaser ──────────────────────────────────── */
function GuideBanner({ isMobile }) {
  return (
    <section style={{ padding: '80px 24px' }}>
      <div style={{
        maxWidth: 1200, margin: '0 auto',
        background: SURF,
        border: `1px solid ${INK}`,
        borderRadius: 20,
        padding: isMobile ? '40px 24px' : '64px 56px',
        display: isMobile ? 'block' : 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 40,
        alignItems: 'center',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', right: -40, top: -40, width: 200, height: 200, borderRadius: '50%', background: HOT, opacity: 0.15, filter: 'blur(30px)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <Kicker>Free resource</Kicker>
          <h2 style={{ fontSize: isMobile ? 28 : 40, fontWeight: 700, letterSpacing: '-0.02em', color: INK, marginBottom: 12 }}>
            New to EU public tenders?
          </h2>
          <p style={{ fontSize: 17, color: INK2, maxWidth: 480, margin: 0 }}>
            Our free guide explains what public tenders are, who can bid, how the process works, and what makes a winning proposal.
          </p>
        </div>
        <div style={{ marginTop: isMobile ? 28 : 0, position: 'relative', zIndex: 1 }}>
          <Link to="/guide" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '13px 24px', borderRadius: 10,
            fontSize: 15, fontWeight: 600,
            background: INK, color: BG,
            textDecoration: 'none',
            boxShadow: `0 4px 0 0 ${ACC}`,
            whiteSpace: 'nowrap',
          }}>
            Read the Tender Guide →
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─── Section: CTA band ──────────────────────────────────────── */
function CTABand({ isMobile }) {
  return (
    <section style={{ padding: '0 24px 72px' }}>
      <div style={{
        maxWidth: 1200, margin: '0 auto',
        background: ACC,
        border: `1px solid ${INK}`,
        borderRadius: 24,
        padding: isMobile ? '48px 24px' : '64px 56px',
        position: 'relative', overflow: 'hidden',
        display: isMobile ? 'block' : 'grid',
        gridTemplateColumns: '1.4fr 1fr',
        gap: 32, alignItems: 'center',
      }}>
        <div style={{ position: 'absolute', right: -60, top: -60, width: 260, height: 260, borderRadius: '50%', background: HOT, opacity: 0.35, pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h2 style={{ fontSize: isMobile ? 30 : 48, fontWeight: 700, letterSpacing: '-0.03em', color: '#fff', marginBottom: 10 }}>
            Stop reading tenders.<br />Start winning them.
          </h2>
          <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.8)', margin: 0 }}>
            Free for 14 days. No credit card. Takes three minutes to set up.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: isMobile ? 'flex-start' : 'flex-end', marginTop: isMobile ? 28 : 0, flexWrap: 'wrap', position: 'relative', zIndex: 1 }}>
          <Link to="/signup" style={{
            display: 'inline-flex', alignItems: 'center',
            padding: '13px 22px', borderRadius: 10,
            fontSize: 15, fontWeight: 600,
            background: INK, color: BG,
            textDecoration: 'none',
            boxShadow: `0 4px 0 0 rgba(0,0,0,0.25)`,
          }}>
            Start free trial →
          </Link>
          <Link to="/pricing" style={{
            display: 'inline-flex', alignItems: 'center',
            padding: '13px 22px', borderRadius: 10,
            fontSize: 15, fontWeight: 500,
            background: 'transparent', color: '#fff',
            border: '1px solid rgba(255,255,255,0.6)',
            textDecoration: 'none',
          }}>
            See pricing
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─── Page ───────────────────────────────────────────────────── */
export default function Home() {
  const width = useWindowWidth();
  const isMobile = width < 768;

  return (
    <div style={{ background: BG }}>
      <Hero isMobile={isMobile} />
      <LogoStrip />
      <Features isMobile={isMobile} />
      <HowItWorks isMobile={isMobile} />
      <GuideBanner isMobile={isMobile} />
      <CTABand isMobile={isMobile} />
      <Footer />
    </div>
  );
}
